/**
 * Shared onboarding mechanics: where the spotlight, arrow and card go, how
 * the card moves between steps, how a line of words swaps, and the peak
 * (docs/ONBOARDING_DESIGN_RULES.md W6, M1, M5).
 *
 * Every duration, distance and easing lives in static/css/onboarding.css and
 * the tokens it reads. JavaScript only measures, places and flips the data
 * attributes those rules key on, then waits for the animations CSS started,
 * so reduced motion (--onboard-motion: 0) needs no code path of its own.
 */

/** Breathing room between the target and the cutout edge, px. */
const CUTOUT_PAD = 6;
/** Space between the arrow tip and the cutout edge, px. */
const ARROW_GAP = 6;
/** Half the arrow's width plus a margin: how close its centre may get to an edge. */
const ARROW_INSET = 24;

/**
 * The spotlight frame for `target`, measured against the `layer` it is drawn
 * on. Both are DOMRect-like ({left, top, width, height}); `radius` is the
 * target's own corner radius, so a pill gets a pill-shaped cutout.
 *
 * The card goes on whichever side of the cutout has more room, the arrow on
 * the card's side pointing from it at the target, so the card never covers
 * the thing it is talking about. A target with too little room either side
 * is scrolled clear of the card first (reveal).
 */
export function spotlightFrame(target, layer, radius) {
    const x = target.left - layer.left - CUTOUT_PAD;
    const y = target.top - layer.top - CUTOUT_PAD;
    const w = target.width + CUTOUT_PAD * 2;
    const h = target.height + CUTOUT_PAD * 2;
    const roomBelow = layer.height - (y + h);
    const side = roomBelow > y ? 'below' : 'above';
    const centre = Math.min(Math.max(x + w / 2, ARROW_INSET), layer.width - ARROW_INSET);
    return {
        x,
        y,
        w,
        h,
        r: radius + CUTOUT_PAD,
        arrow: { side, x: centre, y: side === 'below' ? y + h + ARROW_GAP : y - ARROW_GAP },
        card: side === 'below' ? 'bottom' : 'top',
    };
}

/**
 * Resolves once every animation CSS has started on `element` has finished;
 * `{ subtree: true }` waits on its descendants' too.
 */
export function animationsFinished(element, options = undefined) {
    if (!element) return Promise.resolve();
    return Promise.all(element.getAnimations(options).map((animation) => animation.finished.catch(() => {})));
}

/** New words take `element`'s place: out, the words, in (its [data-swapping] rule). */
export async function swapText(element, text) {
    element.setAttribute("data-swapping", "");
    await animationsFinished(element);
    element.textContent = text;
    element.removeAttribute("data-swapping");
}

/**
 * The scripted walkthrough's timings, in ms, from the tokens on `element`
 * (tokens/semantic.css): one typed character, the pause on each finished
 * field, and how long a picked suggestion stays lit. With reduced motion
 * (--onboard-motion: 0) nothing is typed, so a keystroke takes no time and
 * the pause doubles to give each whole value a moment.
 */
export function onboardTiming(element) {
    const style = getComputedStyle(element);
    const ms = (name) => Number.parseFloat(style.getPropertyValue(name)) || 0;
    const motion = Number.parseFloat(style.getPropertyValue("--onboard-motion"));
    const reduced = motion === 0;
    return {
        reduced,
        keystroke: reduced ? 0 : ms("--dur-onboard-keystroke"),
        hold: ms("--dur-onboard-hold") * (reduced ? 2 : 1),
        pick: ms("--dur-onboard-pick"),
        spotlight: ms("--dur-onboard-spotlight") * (reduced ? 0 : 1),
        read: ms("--dur-onboard-read"),
    };
}

/**
 * How far to scroll so `target` sits in the middle of the part of
 * `scroller` the card leaves uncovered (`cover`: px hidden at its top and
 * bottom). Rects are {top, height}; a positive delta scrolls down.
 */
export function revealDelta(scroller, target, cover) {
    const from = scroller.top + cover.top;
    const to = scroller.top + scroller.height - cover.bottom;
    return target.top + target.height / 2 - (from + to) / 2;
}

/** The nearest ancestor of `element` that scrolls vertically, else the page. */
function scrollerOf(element) {
    for (let node = element.parentElement; node; node = node.parentElement) {
        const overflow = getComputedStyle(node).overflowY;
        if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) return node;
    }
    return document.scrollingElement;
}

/** Whether `scroller` has anything to scroll; the page is scrollerOf's fallback even when it has not. */
function scrolls(scroller) {
    return scroller.scrollHeight > scroller.clientHeight;
}

/**
 * Scroll `target` into the part of the screen `card` does not cover, and
 * resolve once it has settled (scrollend, or after `wait` ms where a browser
 * fires none). `wait` is the caller's, read from the motion tokens, since it
 * outlasts whatever glide runs beside the scroll. Reduced motion jumps.
 *
 * A box that scrolls inside the page (a table's .table-responsive, whose
 * overflow-x makes its overflow-y auto too) first brings the target into
 * itself at once; the outermost scroller then brings it on screen, since the
 * card is fixed to the screen, not to the box.
 */
export function reveal(target, card, { reduced = false, wait }) {
    let scroller = scrollerOf(target);
    for (let outer = scrollerOf(scroller); scroller !== document.scrollingElement && scrolls(outer); outer = scrollerOf(outer)) {
        const inner = revealDelta(scroller.getBoundingClientRect(), target.getBoundingClientRect(), { top: 0, bottom: 0 });
        if (Math.abs(inner) >= 2) scroller.scrollBy({ top: Math.round(inner), behavior: "instant" });
        scroller = outer;
    }
    const page = scroller === document.scrollingElement;
    const box = page ? { top: 0, height: window.innerHeight } : scroller.getBoundingClientRect();
    const cardBox = card && !card.hidden ? card.getBoundingClientRect() : null;
    const cover = { top: 0, bottom: 0 };
    if (cardBox) {
        if (card.dataset.placement === "top") cover.top = Math.max(0, cardBox.bottom - box.top);
        else cover.bottom = Math.max(0, box.top + box.height - cardBox.top);
    }
    const delta = Math.round(revealDelta(box, target.getBoundingClientRect(), cover));
    if (Math.abs(delta) < 2) return Promise.resolve();
    const settled = new Promise((resolve) => {
        const done = () => { clearTimeout(timer); (page ? window : scroller).removeEventListener("scrollend", done); resolve(); };
        const timer = setTimeout(done, reduced ? 0 : wait);
        (page ? window : scroller).addEventListener("scrollend", done, { once: true });
    });
    (page ? window : scroller).scrollBy({ top: delta, behavior: reduced ? "instant" : "smooth" });
    return settled;
}

/**
 * Play the peak (onboard_peak in templates/macros/onboarding.html; M5), then
 * leave it at rest, the still check. Asked again mid-play, it starts over.
 */
export async function playPeak(peak) {
    if (peak.dataset.peak === "play") {
        delete peak.dataset.peak;
        peak.getBoundingClientRect();
    }
    peak.dataset.peak = "play";
    await animationsFinished(peak, { subtree: true });
    if (peak.dataset.peak === "play") delete peak.dataset.peak;
}

/** Leave the peak at rest without playing it: the still check fades in. */
export function settlePeak(peak) {
    delete peak.dataset.peak;
}

/**
 * Slide the card's step content to `incoming` (M1). The outgoing step leaves
 * and the incoming one enters in the same grid cell, so they cross rather
 * than queue; `direction` is 'forward' (slides left) or 'back'. A swap that
 * starts while an earlier step is still leaving drops that step at once, so
 * the card never holds more than the step going and the step coming.
 */
export function swapStep(viewport, incoming, direction) {
    [...viewport.children].filter((child) => child.dataset.motion === 'leave').forEach((child) => child.remove());
    const outgoing = viewport.lastElementChild;
    viewport.dataset.direction = direction;
    incoming.dataset.motion = 'enter';
    if (outgoing) outgoing.dataset.motion = 'leave';
    viewport.append(incoming);
    return Promise.all([
        animationsFinished(outgoing).then(() => outgoing?.remove()),
        animationsFinished(incoming).then(() => {
            if (incoming.dataset.motion === 'enter') delete incoming.dataset.motion;
        }),
    ]);
}

/**
 * Mark the step dots: those before `index` done, `index` current, the rest
 * to do. A card drawn without dots (the offer) passes none.
 */
export function markDots(dots, index) {
    if (!dots) return;
    [...dots.children].forEach((dot, i) => {
        dot.dataset.state = i < index ? 'done' : i === index ? 'current' : 'todo';
    });
}

/**
 * Move the card to the `top` or `bottom` of the layer. It glides there from
 * where it was (a FLIP onto the card's own transition), so a jump between
 * halves still reads as one card moving.
 */
export function placeCard(card, placement) {
    if (card.dataset.placement === placement) return;
    const before = card.getBoundingClientRect().top;
    card.dataset.placement = placement;
    const shift = before - card.getBoundingClientRect().top;
    card.dataset.moving = '';
    card.style.transform = `translateY(${shift}px)`;
    card.getBoundingClientRect();
    delete card.dataset.moving;
    card.style.transform = '';
}
